import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  SparklesIcon,
  TrophyIcon,
  LockClosedIcon,
  CheckCircleIcon,
  StarIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, BadgeIcon, InlineSpinner, EmptyState } from '@/components/ui'
import { gamificationApi } from '@/api'
import type { Badge as BadgeType } from '@/api/gamification'

type BadgeWithDetails = BadgeType & {
  earned_by_count?: number
  requirements?: string[]
}

export default function BadgesPage() {
  const [allBadges, setAllBadges] = useState<BadgeType[]>([])
  const [myBadges, setMyBadges] = useState<BadgeType[]>([])
  const [selectedBadge, setSelectedBadge] = useState<BadgeWithDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [viewMode, setViewMode] = useState<'all' | 'earned' | 'locked'>('all')

  useEffect(() => {
    fetchBadges()
  }, [])

  const fetchBadges = async () => {
    setLoading(true)
    try {
      const [all, my] = await Promise.all([
        gamificationApi.getAllBadges(),
        gamificationApi.getMyBadges(),
      ])
      setAllBadges(all)
      setMyBadges(my)
    } catch (error) {
      console.error('Error fetching badges:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchBadgeDetail = async (code: string) => {
    setLoadingDetail(true)
    try {
      const data = await gamificationApi.getBadge(code)
      setSelectedBadge(data)
    } catch (error) {
      console.error('Error fetching badge detail:', error)
    } finally {
      setLoadingDetail(false)
    }
  }

  const earnedBadgeCodes = new Set(myBadges.map(b => b.code))

  const filteredBadges = allBadges.filter(badge => {
    if (viewMode === 'earned') return earnedBadgeCodes.has(badge.code)
    if (viewMode === 'locked') return !earnedBadgeCodes.has(badge.code)
    return true
  })

  const groupedBadges = filteredBadges.reduce((acc, badge) => {
    if (!acc[badge.type]) acc[badge.type] = []
    acc[badge.type].push(badge)
    return acc
  }, {} as Record<string, BadgeType[]>)

  const typeLabels: Record<string, { label: string; description: string }> = {
    achievement: { label: 'Achievement', description: 'Badge guadagnati per traguardi raggiunti' },
    special: { label: 'Speciali', description: 'Badge rari per azioni speciali' },
    seasonal: { label: 'Stagionali', description: 'Badge disponibili per tempo limitato' },
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  if (loading) {
    return <InlineSpinner message="Caricamento badge..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Badge</h1>
          <p className="text-gray-600">Colleziona badge partecipando alla community</p>
        </div>
        <Link
          to="/gamification/leaderboard"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <TrophyIcon className="h-5 w-5 mr-2" />
          Classifica
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-primary-600">{myBadges.length}</p>
            <p className="text-sm text-gray-500">Badge ottenuti</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">{allBadges.length}</p>
            <p className="text-sm text-gray-500">Badge totali</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">
              {allBadges.length > 0 ? Math.round((myBadges.length / allBadges.length) * 100) : 0}%
            </p>
            <p className="text-sm text-gray-500">Completamento</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-yellow-600">
              {myBadges.reduce((sum, b) => sum + b.points_value, 0)}
            </p>
            <p className="text-sm text-gray-500">Punti da badge</p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Badge Grid */}
        <div className="lg:col-span-2 space-y-6">
          {/* Filter */}
          <Card>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Visualizza:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'all'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Tutti ({allBadges.length})
                </button>
                <button
                  onClick={() => setViewMode('earned')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'earned'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Ottenuti ({myBadges.length})
                </button>
                <button
                  onClick={() => setViewMode('locked')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'locked'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Da sbloccare ({allBadges.length - myBadges.length})
                </button>
              </div>
            </div>
          </Card>

          {/* Badges by Type */}
          {Object.entries(groupedBadges).map(([type, badges]) => (
            <Card key={type}>
              <CardHeader
                title={typeLabels[type]?.label || type}
                subtitle={typeLabels[type]?.description}
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {badges.map((badge) => {
                  const isEarned = earnedBadgeCodes.has(badge.code)

                  return (
                    <button
                      key={badge.id}
                      onClick={() => fetchBadgeDetail(badge.code)}
                      className={`relative p-4 rounded-xl text-center transition-all ${
                        isEarned
                          ? 'bg-gradient-to-br from-primary-50 to-primary-100 hover:shadow-md'
                          : 'bg-gray-50 hover:bg-gray-100 opacity-60'
                      } ${selectedBadge?.code === badge.code ? 'ring-2 ring-primary-500' : ''}`}
                    >
                      {/* Badge Icon */}
                      <div className={`mb-2 ${!isEarned ? 'grayscale' : ''}`}>
                        <BadgeIcon icon={badge.icon} className="w-10 h-10 mx-auto" />
                      </div>

                      {/* Badge Name */}
                      <p className={`text-sm font-medium ${isEarned ? 'text-gray-900' : 'text-gray-500'}`}>
                        {badge.name}
                      </p>

                      {/* Points */}
                      <p className="text-xs text-gray-400 mt-1">
                        +{badge.points_value} punti
                      </p>

                      {/* Lock/Earned Icon */}
                      {isEarned ? (
                        <div className="absolute top-2 right-2">
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        </div>
                      ) : (
                        <div className="absolute top-2 right-2">
                          <LockClosedIcon className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </Card>
          ))}

          {filteredBadges.length === 0 && (
            <Card>
              <EmptyState
                icon={<SparklesIcon className="h-12 w-12" />}
                title={viewMode === 'earned' ? 'Nessun badge ottenuto' : 'Nessun badge'}
                description={
                  viewMode === 'earned'
                    ? 'Partecipa alla community per guadagnare badge'
                    : 'Non ci sono badge disponibili'
                }
              />
            </Card>
          )}
        </div>

        {/* Badge Detail Sidebar */}
        <div className="space-y-6">
          {loadingDetail ? (
            <Card>
              <InlineSpinner message="Caricamento..." />
            </Card>
          ) : selectedBadge ? (
            <Card>
              <div className="text-center space-y-4">
                {/* Badge Icon Large */}
                <div
                  className={`${
                    earnedBadgeCodes.has(selectedBadge.code) ? '' : 'grayscale'
                  }`}
                >
                  <BadgeIcon icon={selectedBadge.icon} className="w-20 h-20 mx-auto" />
                </div>

                {/* Badge Name */}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedBadge.name}</h2>
                  <Badge variant={earnedBadgeCodes.has(selectedBadge.code) ? 'success' : 'default'}>
                    {earnedBadgeCodes.has(selectedBadge.code) ? 'Ottenuto' : 'Non ottenuto'}
                  </Badge>
                </div>

                {/* Description */}
                <p className="text-gray-600">{selectedBadge.description}</p>

                {/* Earned Date */}
                {selectedBadge.earned_at && (
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">Ottenuto il</p>
                    <p className="font-medium text-gray-900">{formatDate(selectedBadge.earned_at)}</p>
                  </div>
                )}

                {/* Points Value */}
                <div className="flex items-center justify-center gap-2 text-primary-600">
                  <StarIcon className="h-5 w-5" />
                  <span className="font-semibold">+{selectedBadge.points_value} punti</span>
                </div>

                {/* Earned By Count */}
                {selectedBadge.earned_by_count !== undefined && (
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">
                      Ottenuto da <span className="font-semibold text-gray-700">{selectedBadge.earned_by_count}</span> utenti
                    </p>
                  </div>
                )}

                {/* Requirements */}
                {selectedBadge.requirements && selectedBadge.requirements.length > 0 && (
                  <div className="pt-4 border-t border-gray-100 text-left">
                    <p className="text-sm font-medium text-gray-700 mb-2">Requisiti:</p>
                    <ul className="space-y-2">
                      {selectedBadge.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircleIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <Card>
              <EmptyState
                icon={<SparklesIcon className="h-12 w-12" />}
                title="Seleziona un badge"
                description="Clicca su un badge per vedere i dettagli"
              />
            </Card>
          )}

          {/* Quick Stats */}
          <Card>
            <CardHeader title="Progressi" />
            <div className="space-y-4">
              {Object.entries(typeLabels).map(([type, info]) => {
                const total = allBadges.filter(b => b.type === type).length
                const earned = myBadges.filter(b => b.type === type).length
                const percentage = total > 0 ? Math.round((earned / total) * 100) : 0

                return (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">{info.label}</span>
                      <span className="text-gray-500">{earned}/{total}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
